// src/pages/visits/MarkVisitWeb.jsx
// -----------------------------------------------------------------------------
// Drop this file into your React web app. It lets a salesperson mark a visit
// from Chrome (desktop/laptop or Android). It uses:
// - HTML5 Geolocation API (works on HTTPS or localhost)
// - Optional webcam selfie capture via getUserMedia
// - Fallback file upload for selfie
// - Customer search (simple debounced fetch)
// - POSTs multipart/form-data to your existing /api/visits route
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from '../../utils/config';
import { useNavigate } from 'react-router-dom';


// --- Small helpers -----------------------------------------------------------
function clsx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// --- Webcam Capture ----------------------------------------------------------
function WebcamCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e) {
        setError(e?.message || "Unable to access camera");
      }
    })();
    return () => {
      // stop camera when component unmounts
      try {
        streamRef.current?.getTracks()?.forEach((t) => t.stop());
      } catch {}
    };
  }, []);

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
        onCapture({ file, previewUrl: URL.createObjectURL(blob) });
      }
    }, "image/jpeg", 0.9);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Camera</h3>
          <button className="text-sm text-gray-600 hover:text-black" onClick={onClose}>Close</button>
        </div>
        {error ? (
          <div className="p-4 text-red-600 text-sm">{error}</div>
        ) : (
          <div className="aspect-video w-full bg-black overflow-hidden rounded-xl">
            <video ref={videoRef} className="w-full h-full object-contain" playsInline muted />
          </div>
        )}
        <div className="mt-4 flex gap-3 justify-end">
          <button className="px-4 py-2 rounded-xl border" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 rounded-xl bg-blue-600 text-white" onClick={handleCapture}>Capture</button>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------
export default function MarkVisitWeb() {
  const token = localStorage.getItem("token");

  // Customer search & selection
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const navigate = useNavigate();


 useEffect(() => {
  const run = async () => {
    if (!debouncedQ || debouncedQ.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      // your API returns { page, limit, total, rows: [...] }
      const r = await axios.get(`${API_BASE_URL}/api/customers`, {
        params: { q: debouncedQ, limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });

      // normalize to an array no matter the shape
      const payload = r.data ?? [];
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.rows)
        ? payload.rows
        : Array.isArray(payload.results)
        ? payload.results
        : Array.isArray(payload.items)
        ? payload.items
        : [];

      // also normalize address fields if your API splits them
      const normalized = list.map((c) => ({
        id: c.id,
        name: c.name,
        address:
          c.address ??
          [c.address, c.locality, c.city].filter(Boolean).join(", "),
      }));

      setResults(normalized);
    } catch (e) {
      console.error(e);
      setResults([]); // keep it an array to avoid .map crash
    } finally {
      setSearching(false);
    }
  };
  run();
}, [debouncedQ, token]);

  // Location
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [acc, setAcc] = useState("");
  const [locStatus, setLocStatus] = useState("idle"); // idle | getting | ok | err
  const [locErr, setLocErr] = useState("");

  const canUseGeo = typeof window !== "undefined" && !!navigator.geolocation;

  const getLocation = () => {
    if (!canUseGeo) {
      setLocErr("Geolocation not supported in this browser");
      setLocStatus("err");
      return;
    }
    setLocStatus("getting");
    setLocErr("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setLat(latitude.toFixed(6));
        setLng(longitude.toFixed(6));
        setAcc(Math.round(accuracy || 0).toString());
        setLocStatus("ok");
      },
      (err) => {
        setLocErr(err?.message || "Failed to get location");
        setLocStatus("err");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Selfie (webcam OR file upload)
  const [showCam, setShowCam] = useState(false);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState("");

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setSelfieFile(f);
      setSelfiePreview(URL.createObjectURL(f));
    }
  };

  const onCaptureFromCam = ({ file, previewUrl }) => {
    setSelfieFile(file);
    setSelfiePreview(previewUrl);
    setShowCam(false);
  };

  useEffect(() => () => {
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
  }, [selfiePreview]);

  // Notes
  const [notes, setNotes] = useState("");

  // Submit
  const [saving, setSaving] = useState(false);
  const canSubmit = !!customer && lat && lng && !saving;

  const save = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("customer_id", String(customer.id));
      fd.append("latitude", String(lat));
      fd.append("longitude", String(lng));
      if (acc) fd.append("accuracy_m", String(acc));
      if (notes) fd.append("notes", notes);
      if (selfieFile) fd.append("selfie", selfieFile);

      const res = await axios.post(`${API_BASE_URL}/api/visit/mark`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

       if (res.data?.duplicate) {
      alert(
        `⚠️ Visit already marked within last 2 hours\nLast visit at: ${new Date(
          res.data.last_visit_at
        ).toLocaleString()}`
      );
    } else {
      alert("Visit saved ✅");
    }
      // reset minimal
      setNotes("");
      setSelfieFile(null);
      setSelfiePreview("");
      navigate('/visits');
    } catch (e) {
      console.error(e);
      alert("Failed to save visit");
    } finally {
      setSaving(false);
    }
  };

  // --- UI --------------------------------------------------------------------
  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-2xl font-semibold mb-4">Mark Visit (Web)</h1>

      {/* Customer */}
      <div className="rounded-2xl border p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-medium">Customer</h2>
        </div>
        {customer ? (
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold">{customer.name}</div>
              {customer.address && <div className="text-sm text-gray-600">{customer.address}</div>}
            </div>
            <button
              className="px-3 py-1.5 rounded-xl border text-sm"
              onClick={() => setCustomer(null)}
            >Change</button>
          </div>
        ) : (
          <div>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer by name / address"
              className="w-full rounded-xl border px-3 py-2"
            />
            <div className="mt-2 max-h-64 overflow-auto rounded-xl border divide-y">
              {searching && <div className="p-3 text-sm text-gray-500">Searching…</div>}
              {!searching && results.length === 0 && q.length >= 2 && (
                <div className="p-3 text-sm text-gray-500">No results</div>
              )}
              {results.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCustomer(c)}
                  className="w-full text-left p-3 hover:bg-gray-50"
                >
                  <div className="font-medium">{c.name}</div>
                  {c.address && <div className="text-xs text-gray-600">{c.address}</div>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Location */}
      <div className="rounded-2xl border p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-medium">Location</h2>
          <div className="text-xs text-gray-500">{window.isSecureContext ? "Secure context ✓" : "Use HTTPS for best accuracy"}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Latitude</label>
            <input value={lat} onChange={(e) => setLat(e.target.value)} className="w-full rounded-xl border px-3 py-2" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Longitude</label>
            <input value={lng} onChange={(e) => setLng(e.target.value)} className="w-full rounded-xl border px-3 py-2" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Accuracy (m)</label>
            <input value={acc} onChange={(e) => setAcc(e.target.value)} className="w-full rounded-xl border px-3 py-2" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={getLocation}
            className={clsx(
              "px-4 py-2 rounded-xl border",
              locStatus === "getting" && "opacity-60 cursor-not-allowed"
            )}
            disabled={locStatus === "getting"}
          >
            {locStatus === "getting" ? "Getting location…" : "Use Current Location"}
          </button>
          {locStatus === "ok" && <span className="text-sm text-green-700">Location captured ✓</span>}
          {locStatus === "err" && <span className="text-sm text-red-600">{locErr}</span>}
        </div>
        <p className="mt-2 text-xs text-gray-500">Tip: Desktop geolocation may be coarse. If GPS is inaccurate, you can manually adjust lat/lng.</p>
      </div>

      {/* Selfie */}
      <div className="rounded-2xl border p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-medium">Selfie (optional)</h2>
          <div className="text-xs text-gray-500">Proof of visit</div>
        </div>
        {selfiePreview ? (
          <div>
            <img src={selfiePreview} alt="selfie" className="w-full max-h-72 object-contain rounded-xl" />
            <div className="mt-3 flex gap-3">
              <button className="px-3 py-1.5 rounded-xl border" onClick={() => setShowCam(true)}>Retake</button>
              <button className="px-3 py-1.5 rounded-xl border" onClick={() => { setSelfieFile(null); setSelfiePreview(""); }}>Remove</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <button className="px-4 py-2 rounded-xl border" onClick={() => setShowCam(true)}>Open Camera</button>
            <label className="px-4 py-2 rounded-xl border cursor-pointer">
              Upload Photo
              <input type="file" accept="image/*" className="hidden" onChange={onPickFile} />
            </label>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="rounded-2xl border p-4 mb-4">
        <h2 className="text-lg font-medium mb-2">Notes (optional)</h2>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full rounded-xl border px-3 py-2"
          placeholder="Any remarks about this visit…"
        />
      </div>

      {/* Submit */}
      <div className="flex items-center justify-end">
        <button
          onClick={save}
          disabled={!canSubmit}
          className={clsx(
            "px-6 py-2 rounded-xl text-white",
            canSubmit ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"
          )}
        >
          {saving ? "Saving…" : "Save Visit"}
        </button>
      </div>

      {showCam && (
        <WebcamCapture onCapture={onCaptureFromCam} onClose={() => setShowCam(false)} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Server notes:
// - Ensure CORS is enabled for your frontend origin if web and API are on
//   different hosts.
// - Geolocation requires HTTPS in production (or works on http://localhost).
// - This page posts to POST /api/visits with fields: customer_id, latitude,
//   longitude, accuracy_m (optional), notes (optional), selfie (optional file).
// - It expects Authorization: Bearer <token> to be valid.
// -----------------------------------------------------------------------------
